import { useNavigate } from "react-router";
import { keepPreviousData, skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type PageOptions, usePage } from "@/api/pagination";
import RecipientsService from "@/api/services/recipients";
import { type ExistingRecipient, Recipient, SourceCredential, UpdateRecipientPayload } from "@/types";

export function useRecipients(params: PageOptions = {}, filters: Record<string, string> = {}) {
  return usePage<ExistingRecipient>(
    ["recipients", "list", filters],
    (paginationParams) => RecipientsService.getRecipients(paginationParams, filters),
    params,
    { placeholderData: keepPreviousData },
  );
}

export function useAllRecipients() {
  return useQuery({
    queryKey: ["recipients", "all"],
    queryFn: () => RecipientsService.getAllRecipients(),
  });
}

export function useRecipient(recipientId: ExistingRecipient["id"] | undefined) {
  return useQuery({
    queryKey: ["recipients", "detail", recipientId],
    queryFn: recipientId ? () => RecipientsService.getRecipient(recipientId) : skipToken,
  });
}

export function useSourceCredentials() {
  return useQuery({
    queryKey: ["source-credentials"],
    queryFn: () => RecipientsService.getSourceCredentials(),
  });
}

export function useTestSourceCredentials() {
  return useMutation({
    mutationFn: (credential: SourceCredential) => RecipientsService.testSourceCredentials(credential),
  });
}

export function useCreateRecipient() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { recipient: Recipient; redirectPath?: string }) =>
      RecipientsService.postRecipient(payload.recipient),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["recipients"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useUpdateRecipient() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: UpdateRecipientPayload & { redirectPath?: string }) =>
      RecipientsService.patchRecipient(payload.recipientID, payload.fields!),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["recipients"] });
      queryClient.invalidateQueries({
        queryKey: ["recipients", variables.recipientID],
      });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useDeleteRecipient() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { recipientID: ExistingRecipient["id"]; redirectPath?: string }) =>
      RecipientsService.deleteRecipient(payload.recipientID),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["recipients"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useCreateSourceCredential() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { sourceCredential: SourceCredential; redirectPath?: string }) =>
      RecipientsService.postSourceCredential(payload.sourceCredential),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["source-credentials"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}
